/**
 * Haftalık Vardiya Planlama Ekranı.
 */

import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  ChevronLeft, ChevronRight, Calendar, Send, Copy, Trash2,
  CheckCircle2, AlertTriangle, Plus, Sun, Lock,
} from 'lucide-react'
import {
  haftaVardiyalariGetir,
  haftayiYayinla,
  haftayiKopyala,
  haftayiTemizle,
  type Vardiya,
} from '@/lib/services/vardiya-service'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumKaliplariGetir, type VardiyaKalibi } from '@/lib/services/vardiya-kalibi-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { useAuth } from '@/contexts/AuthContext'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { VardiyaAtaModal } from '@/components/vardiya/VardiyaAtaModal'
import {
  GUN_ADLARI,
  buHaftaninPazartesi,
  ekleGun,
  haftaTarihleri,
  haftaEtiketi,
  tarihStr,
  bugunMu,
} from '@/lib/tarih'
import type { KullaniciPersonel, Sube, Pozisyon } from '@/types'

interface Props {
  subeler: Sube[]
}

export function HaftalikPlanlama({ subeler }: Props) {
  const { firebaseUser, kullanici } = useAuth()
  const isBoss = kullanici?.rol === 'boss'
  const [haftaBaslangic, setHaftaBaslangic] = useState<Date>(buHaftaninPazartesi())
  const [seciliSubeId, setSeciliSubeId] = useState<string>(subeler[0]?.id ?? '')

  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [kaliplar, setKaliplar] = useState<VardiyaKalibi[]>([])
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [vardiyalar, setVardiyalar] = useState<Vardiya[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [islemde, setIslemde] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [bilgi, setBilgi] = useState<string | null>(null)

  const [atamaAcik, setAtamaAcik] = useState(false)
  const [atamaPersonel, setAtamaPersonel] = useState<KullaniciPersonel | null>(null)
  const [atamaTarih, setAtamaTarih] = useState<string>('')
  const [atamaVardiya, setAtamaVardiya] = useState<Vardiya | null>(null)

  const [yayinlaOnay, setYayinlaOnay] = useState(false)
  const [kopyalaOnay, setKopyalaOnay] = useState(false)
  const [temizleOnay, setTemizleOnay] = useState(false)

  const haftaBaslangicStr = useMemo(() => tarihStr(haftaBaslangic), [haftaBaslangic])
  const gunler = useMemo(() => haftaTarihleri(haftaBaslangic), [haftaBaslangic])

  // Hafta konumu: 0 = bu hafta, <0 = geçmiş, >0 = gelecek
  const haftaFarki = useMemo(() => {
    const buPzt = buHaftaninPazartesi()
    return Math.round((haftaBaslangic.getTime() - buPzt.getTime()) / (7 * 24 * 60 * 60 * 1000))
  }, [haftaBaslangic])
  const gecmisHafta = haftaFarki < 0
  // Yönetici geçmiş haftayı düzenleyemez; BOSS her zaman düzenleyebilir.
  const kilitli = gecmisHafta && !isBoss

  const yukle = useCallback(async () => {
    if (!seciliSubeId) return
    try {
      setYukleniyor(true)
      const [p, k, poz, v] = await Promise.all([
        tumPersonelleriGetir(),
        tumKaliplariGetir(),
        tumPozisyonlariGetir(),
        haftaVardiyalariGetir(haftaBaslangicStr, seciliSubeId),
      ])
      setPersoneller(p.filter((x) => x.aktif && x.subeIdleri.includes(seciliSubeId)))
      setKaliplar(k.filter((x) => x.aktif))
      setPozisyonlar(poz.filter((x) => x.aktif))
      setVardiyalar(v)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Yükleme hatası: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }, [seciliSubeId, haftaBaslangicStr])

  useEffect(() => {
    yukle()
  }, [yukle])

  const vardiyaHaritasi = useMemo(() => {
    const m = new Map<string, Vardiya>()
    vardiyalar.forEach((v) => m.set(`${v.personelId}|${v.tarih}`, v))
    return m
  }, [vardiyalar])

  const pozisyonAdi = useCallback(
    (id?: string) => {
      if (!id) return null
      return pozisyonlar.find((p) => p.id === id)?.isim
    },
    [pozisyonlar]
  )

  function hucreyeTikla(personel: KullaniciPersonel, tarih: Date) {
    if (kilitli) return  // yönetici geçmiş haftayı düzenleyemez
    const tStr = tarihStr(tarih)
    const mevcut = vardiyaHaritasi.get(`${personel.id}|${tStr}`) ?? null
    setAtamaPersonel(personel)
    setAtamaTarih(tStr)
    setAtamaVardiya(mevcut)
    setAtamaAcik(true)
  }

  async function yayinla() {
    setYayinlaOnay(false)
    setIslemde(true)
    setHata(null)
    setBilgi(null)
    try {
      const sayi = await haftayiYayinla(haftaBaslangicStr, seciliSubeId)
      setBilgi(sayi === 0 ? 'Yayınlanacak taslak yok.' : `${sayi} kayıt yayınlandı 🎉`)
      await yukle()
    } catch (err) {
      console.error(err)
      setHata('Yayınlanamadı: ' + (err as Error).message)
    } finally {
      setIslemde(false)
    }
  }

  async function kopyala() {
    setKopyalaOnay(false)
    if (!firebaseUser) return
    setIslemde(true)
    setHata(null)
    setBilgi(null)
    try {
      const oncekiHafta = tarihStr(ekleGun(haftaBaslangic, -7))
      const sayi = await haftayiKopyala(
        oncekiHafta, haftaBaslangicStr, seciliSubeId, firebaseUser.uid
      )
      setBilgi(`${sayi} vardiya kopyalandı.`)
      await yukle()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIslemde(false)
    }
  }

  async function temizle() {
    setTemizleOnay(false)
    setIslemde(true)
    setHata(null)
    setBilgi(null)
    try {
      const sayi = await haftayiTemizle(haftaBaslangicStr, seciliSubeId)
      setBilgi(`${sayi} kayıt silindi.`)
      await yukle()
    } catch (err) {
      setHata('Silinemedi: ' + (err as Error).message)
    } finally {
      setIslemde(false)
    }
  }

  const taslakSayisi = vardiyalar.filter((v) => !v.yayinlandi).length
  const yayinSayisi = vardiyalar.filter((v) => v.yayinlandi).length

  if (subeler.length === 0) {
    return (
      <div className="card text-center py-12">
        <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="font-bold text-gray-900 mb-2">Şube yok</h3>
        <p className="text-gray-600">Önce şube ekle.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <select
          value={seciliSubeId}
          onChange={(e) => setSeciliSubeId(e.target.value)}
          className="input-base md:w-56"
        >
          {subeler.map((s) => (
            <option key={s.id} value={s.id}>{s.isim}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl px-1 flex-1 md:flex-none">
          <button
            onClick={() => setHaftaBaslangic(ekleGun(haftaBaslangic, -7))}
            className="p-2 hover:bg-gray-100 rounded-lg"
            disabled={islemde}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center px-2 py-1.5">
            <p className="text-sm font-semibold text-gray-900">
              {haftaEtiketi(haftaBaslangic)}
            </p>
            <span
              className={`inline-block mt-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                haftaFarki === 0
                  ? 'bg-green-100 text-green-700'
                  : haftaFarki < 0
                    ? 'bg-red-100 text-red-700'
                    : 'bg-blue-100 text-blue-700'
              }`}
            >
              {haftaFarki === 0
                ? 'Bu Hafta'
                : haftaFarki === -1
                  ? 'Geçen Hafta'
                  : haftaFarki === 1
                    ? 'Gelecek Hafta'
                    : haftaFarki < 0
                      ? `${-haftaFarki} hafta önce`
                      : `${haftaFarki} hafta sonra`}
            </span>
          </div>
          <button
            onClick={() => setHaftaBaslangic(ekleGun(haftaBaslangic, 7))}
            className="p-2 hover:bg-gray-100 rounded-lg"
            disabled={islemde}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setHaftaBaslangic(buHaftaninPazartesi())}
            className="px-3 py-1.5 text-xs text-brand-600 hover:underline whitespace-nowrap"
            disabled={islemde}
          >
            Bu hafta
          </button>
        </div>
      </div>

      {kilitli && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm flex items-center gap-2">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Geçmiş hafta — düzenlenemez.</strong> Bu haftada değişiklik gerekiyorsa BOSS'a başvur.
          </span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-3">
        <div className="text-sm text-gray-700">
          <span className="font-semibold">{vardiyalar.length}</span> kayıt
          {' • '}
          <span className="text-amber-700">{taslakSayisi} taslak</span>
          {' / '}
          <span className="text-green-700">{yayinSayisi} yayında</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setKopyalaOnay(true)}
            className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-100 rounded-lg text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={islemde || kilitli}
          >
            <Copy className="w-4 h-4" />
            <span>Geçen Haftayı Kopyala</span>
          </button>
          <button
            onClick={() => setTemizleOnay(true)}
            className="px-3 py-1.5 bg-white border border-red-200 text-red-700 hover:bg-red-50 rounded-lg text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={islemde || kilitli || vardiyalar.length === 0}
          >
            <Trash2 className="w-4 h-4" />
            <span>Haftayı Temizle</span>
          </button>
          <button
            onClick={() => setYayinlaOnay(true)}
            className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={islemde || kilitli || taslakSayisi === 0}
          >
            <Send className="w-4 h-4" />
            <span>Taslakları Yayınla ({taslakSayisi})</span>
          </button>
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {hata}
        </div>
      )}
      {bilgi && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-3 text-sm flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          {bilgi}
        </div>
      )}

      {yukleniyor ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      ) : personeller.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600">Bu şubeye atanmış personel yok.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-[120px]">
                  Personel
                </th>
                {gunler.map((g, i) => {
                  const aktif = bugunMu(g)
                  return (
                    <th
                      key={i}
                      className={`text-center px-2 py-2 font-semibold border-l border-gray-200 min-w-[110px] ${
                        aktif ? 'bg-brand-50 text-brand-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="text-xs">{GUN_ADLARI[i]}</div>
                      <div className="text-base font-bold">{g.getDate()}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {personeller.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-25">
                  <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white border-r border-gray-100">
                    <div className="truncate max-w-[160px]">{p.isim}</div>
                  </td>
                  {gunler.map((g, i) => {
                    const tStr = tarihStr(g)
                    const v = vardiyaHaritasi.get(`${p.id}|${tStr}`)
                    return (
                      <td
                        key={i}
                        onClick={() => hucreyeTikla(p, g)}
                        className={`px-1 py-1 border-l border-gray-100 align-top ${
                          kilitli ? 'cursor-default' : 'cursor-pointer hover:bg-brand-50'
                        }`}
                      >
                        {v ? (
                          v.durum === 'izinli' ? (
                            <div
                              className={`p-1.5 rounded-lg text-xs flex items-center gap-1 ${
                                v.yayinlandi
                                  ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                  : 'bg-amber-50 text-amber-900 border border-amber-200'
                              }`}
                            >
                              <Sun className="w-3 h-3 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="font-semibold">İzinli</div>
                                {!v.yayinlandi && (
                                  <div className="text-[10px] italic opacity-60">taslak</div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`p-1.5 rounded-lg text-xs ${
                                v.yayinlandi
                                  ? 'bg-green-100 text-green-900 border border-green-200'
                                  : 'bg-blue-50 text-blue-900 border border-blue-200'
                              }`}
                            >
                              <div className="font-semibold whitespace-nowrap">
                                {v.baslangic}-{v.bitis}
                              </div>
                              {pozisyonAdi(v.pozisyonId) && (
                                <div className="text-[10px] opacity-80 truncate">
                                  {pozisyonAdi(v.pozisyonId)}
                                </div>
                              )}
                              {!v.yayinlandi && (
                                <div className="text-[10px] italic opacity-60">taslak</div>
                              )}
                            </div>
                          )
                        ) : (
                          <div className="flex items-center justify-center h-10 text-gray-300 hover:text-brand-500">
                            {!kilitli && <Plus className="w-4 h-4" />}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {atamaPersonel && (
        <VardiyaAtaModal
          acik={atamaAcik}
          mevcutVardiya={atamaVardiya}
          personel={atamaPersonel}
          subeId={seciliSubeId}
          tarih={atamaTarih}
          kaliplar={kaliplar}
          pozisyonlar={pozisyonlar}
          onKapat={() => setAtamaAcik(false)}
          onKaydedildi={() => {
            setAtamaAcik(false)
            yukle()
          }}
        />
      )}

      <ConfirmDialog
        acik={yayinlaOnay}
        baslik="Taslakları Yayınla"
        mesaj={`${taslakSayisi} taslak kayıt yayınlanacak ve personel görecek. Devam mı?`}
        onayMetni="Yayınla"
        onOnay={yayinla}
        onIptal={() => setYayinlaOnay(false)}
      />

      <ConfirmDialog
        acik={kopyalaOnay}
        baslik="Geçen Haftayı Kopyala"
        mesaj="Geçen haftanın vardiyaları bu haftaya taslak olarak kopyalanacak. (İzinli günler kopyalanmaz.) Devam mı?"
        onayMetni="Kopyala"
        onOnay={kopyala}
        onIptal={() => setKopyalaOnay(false)}
      />

      <ConfirmDialog
        acik={temizleOnay}
        baslik="Haftayı Temizle"
        mesaj={`Bu haftaki ${vardiyalar.length} kayıt silinecek (yayınlananlar dahil). Devam mı?`}
        onayMetni="Sil"
        tehlikeli
        onOnay={temizle}
        onIptal={() => setTemizleOnay(false)}
      />
    </div>
  )
}
